import { GrundartikelLink } from '@/components/catalog/GrundartikelLink'
import { useRef, useState } from 'react'
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import type { CreateRolloutAck, FamilyVariante, RolloutFamily } from '@/api/priceRollout'
import { fmtEUR } from '@/lib/pricing-format'
import { cn } from '@/lib/utils'
import { FOLGE } from './RolloutDialoge'
import { portalName } from './rolloutView'
import { sammelSerie, sammelZusammenfassung, type SammelEintrag, type SammelErgebnis } from './sammelstart'

/** Eine ausgewählte Variante mit dem beim Öffnen bestätigten Stand (`bestaetigt` = startStand(start.base)). */
export interface SammelAuswahl extends SammelEintrag { v: FamilyVariante }

const ERGEBNIS_FARBE: Record<SammelErgebnis['art'], string> = {
  ok: 'text-success', warnung: 'text-ink', fehler: 'text-coral', unklar: 'text-coral',
  veraltet: 'text-ink-2', notaus: 'text-ink-2', angehalten: 'text-ink-2', abgebrochen: 'text-ink-2',
}

/**
 * Bestätigung und Ablauf des Sammelstarts (Plan 2026-10-27-rollout-sammelstart): jede Variante einzeln
 * sichtbar, Summenzeile LIVE, Start strikt nacheinander mit Reload je Variante, „Anhalten“ vor der nächsten.
 */
export function SammelstartDialog({ auswahl, liveWrites, sperre, ladeFamilie, starte, onSchliessen }: {
  auswahl: SammelAuswahl[] | null
  liveWrites: boolean
  sperre: string | null
  ladeFamilie: () => Promise<RolloutFamily>
  starte: (sku: string, portal: string, ziel: number) => Promise<CreateRolloutAck>
  /** `gelaufen`: mindestens eine Variante wurde gesendet — Familie neu laden, Auswahl leeren. */
  onSchliessen: (gelaufen: boolean) => void
}) {
  const [ergebnisse, setErgebnisse] = useState<SammelErgebnis[] | null>(null)
  const [laeuft, setLaeuft] = useState(false)
  const laeuftRef = useRef(false)
  const anhalten = useRef(false)
  const [haltAngefordert, setHaltAngefordert] = useState(false)
  const n = auswahl?.length ?? 0
  const live = auswahl?.filter((a) => !a.v.start.base.alle_dry_run).length ?? 0
  const fertig = ergebnisse !== null && !laeuft

  const zuruecksetzen = () => { setErgebnisse(null); setLaeuft(false); laeuftRef.current = false; anhalten.current = false; setHaltAngefordert(false) }
  const schliessen = () => {
    if (laeuftRef.current) return
    const gelaufen = ergebnisse !== null
    zuruecksetzen()
    onSchliessen(gelaufen)
  }
  const starten = async () => {
    if (!auswahl || laeuftRef.current || ergebnisse !== null || sperre !== null) return
    laeuftRef.current = true
    setLaeuft(true)
    setErgebnisse([])
    try {
      await sammelSerie({
        eintraege: auswahl.map(({ sku, bestaetigt }) => ({ sku, bestaetigt })),
        ladeFamilie, starte, angehalten: () => anhalten.current,
        onErgebnis: (e) => setErgebnisse((alt) => [...(alt ?? []), e]),
      })
    } finally {
      laeuftRef.current = false
      setLaeuft(false)
    }
  }
  const ergebnisVon = (sku: string) => ergebnisse?.find((e) => e.sku === sku)

  return (
    <Dialog open={auswahl !== null} onOpenChange={(o) => { if (!o) schliessen() }}>
      <DialogContent className="sm:max-w-2xl">
        {auswahl && (
          <>
            <DialogHeader>
              <DialogTitle className="font-display">Basis-Rollouts für {n} Varianten starten</DialogTitle>
              <DialogDescription>
                Jede Variante startet einzeln über den normalen Start, nacheinander. Vor jedem Start werden die Daten neu
                geladen; hat sich eine Variante geändert, wird sie übersprungen. Der Server prüft jeden Start erneut.
              </DialogDescription>
            </DialogHeader>
            <div
              role="status"
              className={cn('rounded-md border-l-4 px-3 py-2 text-sm text-ink', live > 0 ? 'border-coral bg-coral-soft font-semibold' : 'border-success bg-success-soft')}
            >
              {`${n} Rollouts, davon ${live} LIVE`}
              {live > 0 && !liveWrites && <span className="ml-1 font-normal">(globale LIVE-Freigabe aus – schreibt derzeit nur ins Protokoll)</span>}
            </div>
            <ul className="max-h-[50vh] space-y-1.5 overflow-y-auto">
              {auswahl.map(({ v }) => {
                const s = v.start.base
                const basis = s.scopes.find((x) => x.portal === 'base')
                const e = ergebnisVon(v.sku)
                return (
                  <li
                    key={v.sku} data-testid={`sammel-${v.sku}`}
                    className={cn('rounded-md border border-line bg-panel px-3 py-2 border-l-4', s.alle_dry_run ? 'border-l-line' : 'border-l-coral')}
                  >
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
                      <GrundartikelLink code={v.kennung?.stern ?? v.sku} className="font-mono font-semibold text-ink" />
                      {v.kennung?.stern && <span className="font-mono text-xs text-muted">{v.sku}</span>}
                      <span className="font-mono text-ink">{fmtEUR(v.spuren.base.ist_preis)} → <b>{fmtEUR(s.ziel)}</b></span>
                      <span className="text-xs text-ink-2">
                        {s.fanout_portal === 'ebay' ? 'Basis → eBay gekoppelt' : s.fanout_portal ? `Basis → ${portalName(s.fanout_portal)}` : 'Basis'}
                      </span>
                      {s.alle_dry_run
                        ? <span className="rounded bg-panel-2 px-1.5 py-px text-[11px] text-ink">nur Protokoll</span>
                        : <span className="rounded bg-coral px-1.5 py-px text-[11px] font-bold tracking-wide text-ink">LIVE</span>}
                      {basis && <span className="ml-auto text-xs text-ink-2">{FOLGE[basis.folge]}</span>}
                    </div>
                    {e && <div className={cn('mt-1 text-xs font-medium', ERGEBNIS_FARBE[e.art])}>{e.text}</div>}
                  </li>
                )
              })}
            </ul>
            {laeuft && (
              <p role="status" className="text-sm text-ink-2">
                {haltAngefordert ? 'Wird nach der laufenden Variante angehalten…' : `Läuft: ${Math.min((ergebnisse?.length ?? 0) + 1, n)} von ${n}`}
              </p>
            )}
            {fertig && <p role="status" className="text-sm font-semibold text-ink">{sammelZusammenfassung(ergebnisse)}</p>}
            {sperre && ergebnisse === null && <p className="rounded-md bg-panel-2 px-3 py-2 text-sm text-ink-2">{sperre}</p>}
            <DialogFooter>
              {laeuft ? (
                <Button variant="outline" disabled={haltAngefordert} onClick={() => { anhalten.current = true; setHaltAngefordert(true) }}>Anhalten</Button>
              ) : fertig ? (
                <Button onClick={schliessen}>Schließen</Button>
              ) : (
                <>
                  <Button variant="outline" onClick={schliessen}>Abbrechen</Button>
                  <Button onClick={() => void starten()} disabled={sperre !== null || n === 0}>{n} Rollouts starten</Button>
                </>
              )}
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}
